<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TextArea from '@/components/partials/Inputs/TextArea.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import Headline from '@/components/partials/Headline.vue'
import ModalNuevo from '@/components/Modals/ModalNuevo/ModalNuevo.vue'
import type { BasketProduct } from '@/types/http/BasketProduct'
import { useCartStore } from "@/stores/useCartStore"

const cartStore = useCartStore()

const props = defineProps<{
  specialInstructionPlaceholder: string | null
  product: BasketProduct | null
  open: boolean
}>()

const { t } = useI18n()
const emit = defineEmits([ 'close' ])

const instructions = ref<string>('')

instructions.value = props.product?.instructions ?? ''

async function updateInstructions(): void {
  await cartStore.setBasketItemInstructions(props.product?.hash, instructions.value)

  instructions.value = ''

  emit('close')
}

const error = computed<boolean>(() => instructions.value ? instructions.value.length > 100 : false)
</script>

<template>
  <ModalNuevo class="instructions-modal"
              data-test-id="product-remark-modal"
              :open="open"
              @close="emit('close')">
    <template #modal-header>
      <Headline class="instructions-modal__title">
        {{ t('cart.add_instructions_to', { product: props.product.title }) }}
      </Headline>
    </template>

    <TextArea v-model="instructions"
              name="instructions"
              grow
              data-test-id="product-remark-textarea"
              :placeholder="specialInstructionPlaceholder ?? t('cart.remarkplaceholder')"
              :error="error ? t('cart.remark_error') : null"
              class="checkout-form-instructions__text-area"/>

    <template #modal-bottom>
      <Button align-right
              data-test-id="remark-modal-save"
              :disabled="error"
              @click="updateInstructions">
        {{ t('buttons.save') }}
      </Button>
    </template>
  </ModalNuevo>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.instructions-modal {
  &__title {
    font-weight: 700;
    font-size: 1.2rem;
    padding:   2rem 2rem 0;
  }
}
</style>
